import { Box, Chip, Typography } from '@mui/material';
import { getNasemScoreConfig } from '../../../utils/reports';
import { formatDateWithOrdinalSuffix } from '../../../utils';
import { useThemeContext } from '../../../contexts/BaseThemeContextProvider';

export const ResultTimelineNasemTooltip = ({ reportData }: any) => {
  const { theme } = useThemeContext();
  const { exposureTextColor, badgeBackgroundColor, badgeTextColor } = getNasemScoreConfig(
    Number(reportData.nasemScore),
    theme,
  );

  return (
    <Box
      sx={{
        position: 'relative',
        background: theme.custom.report.timeline.nasemScoreTimelineChart.tooltip.backgroundColor,
        borderRadius: '8px',
        border: `2px solid ${theme.custom.report.timeline.nasemScoreTimelineChart.tooltip.borderColor}`,
        padding: '12px',
        display: 'flex',
        flexDirection: 'column',
        gap: '8px',
        maxWidth: 300,
        boxShadow: '0px 4px 6px rgba(0, 0, 0, 0.2)',
      }}
    >
      {/* Content */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Chip
          label={formatDateWithOrdinalSuffix(reportData.date, {
            month: 'short',
            day: 'numeric',
            year: 'numeric',
          })}
          sx={{
            bgcolor: theme.custom.report.timeline.nasemScoreTimelineChart.tooltip.dateChip.backgroundColor,
            borderRadius: '4px',
            color: theme.custom.report.timeline.nasemScoreTimelineChart.tooltip.dateChip.color,
            fontWeight: 500,
            fontSize: '14px',
            '& .MuiChip-label': {
              padding: '0',
              marginRight: '4px',
            },
            marginRight: '4px',
          }}
        />
        <Typography
          sx={{
            fontSize: '12px',
            fontWeight: 400,
            color: exposureTextColor,
            marginLeft: 'auto',
          }}
        >
          NASEM level {reportData.nasemLevel}
        </Typography>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            backgroundColor: badgeBackgroundColor,
            borderRadius: '16px',
            padding: '2px 8px',
            marginLeft: '4px',
          }}
        >
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 700,
              lineHeight: '16px',
              color: badgeTextColor,
            }}
          >
            {Number(reportData.nasemScore).toFixed(1)}
          </Typography>
        </Box>
      </Box>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        {/* Compounds Detected */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexBasis: { xs: '45%', sm: 'auto' },
          }}
        >
          <Typography
            sx={{
              fontSize: '28px',
              fontWeight: 400,
              lineHeight: '36px',
              color:
                theme.custom.report.timeline.nasemScoreTimelineChart.tooltip.compoundsDetected.detectedCountTextColor,
              marginRight: '4px',
            }}
          >
            {reportData.totalCompoundsDetected}
          </Typography>
          <Typography
            sx={{
              fontSize: '12px',
              fontWeight: 400,
              color:
                theme.custom.report.timeline.nasemScoreTimelineChart.tooltip.compoundsDetected
                  .compoundDetectedTextColor,
              lineHeight: '14px',
              marginTop: '-3px',
            }}
          >
            PFAS above <br /> detection level
          </Typography>
        </Box>

        {/* Divider Line */}
        <Box
          sx={{
            width: '2px',
            height: '40px',
            margin: '0 8px',
            backgroundColor: theme.custom.report.timeline.nasemScoreTimelineChart.tooltip.dividerLineColor,
            borderRadius: '4px',
          }}
        />

        {/* Total PFAS */}
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexBasis: { xs: '40%', sm: 'auto' },
            justifyContent: 'flex-end',
            gap: '4px',
          }}
        >
          <Typography
            sx={{
              fontSize: '28px',
              fontWeight: 400,
              lineHeight: '36px',
              color: theme.custom.report.timeline.nasemScoreTimelineChart.tooltip.totalPfas.totalPfasScoreTextColor,
            }}
          >
            {Number(reportData.totalPFASScore).toFixed(1)}
          </Typography>
          <Typography
            sx={{
              color: theme.custom.report.timeline.nasemScoreTimelineChart.tooltip.totalPfas.pfasUnitColor,
              fontSize: '12px',
              fontWeight: 400,
              lineHeight: '18px',
              marginTop: '4px',
            }}
          >
            ng/mL{' '}
            <Typography
              component="span"
              sx={{
                display: 'block',
                color: theme.custom.report.timeline.nasemScoreTimelineChart.tooltip.totalPfas.totalPfasTextColor,
                fontSize: '12px',
                fontWeight: 400,
                lineHeight: '18px',
                textDecorationLine: 'underline',
                textDecorationStyle: 'solid',
                textDecorationSkipInk: 'auto',
                textDecorationThickness: 'auto',
                textUnderlineOffset: 'auto',
                textUnderlinePosition: 'from-font',
              }}
            >
              total PFAS measurable
            </Typography>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};
